import * as THREE from 'three'
import { Truck, ControllBox, ControllBoxStep } from 'types/data'
import anime from 'animejs'
import { BOX_MESHES, BOX_LIGHTS, SWITCH_MESHES } from 'constants/threeConstants'
import { AnimationAction } from 'three/src/animation/AnimationAction'

declare global {
  interface Window { 
    Draggable: any,
  }
}

const Draggable = window.Draggable

type ControllBoxProps = {
  truckData: Truck,
  truck: THREE.Scene,
  truckAnimations: THREE.AnimationClip[],
  bindRaycaster: Function,
  bindDom: Function,
  onAnimationEnd: Function
}

export default class ControllBoxController {
  protected props: ControllBoxProps
  protected boxMeshes: THREE.Object3D[]
  protected actions: {
    action: AnimationAction
    meshName: string,
    light?: string
  }[] = []
  protected inited = false
  lastActiveStepData?: ControllBoxStep
  mixer: THREE.AnimationMixer
  knobMixer: THREE.AnimationMixer
  tl: any
  drag: any
  trackAction?: AnimationAction
  switchClickedSide?: string
  
  constructor(props: ControllBoxProps) {
    this.props = props
    this.boxMeshes = []
    this.mixer = new THREE.AnimationMixer(props.truck)
    this.knobMixer = new THREE.AnimationMixer(props.truck)
    
    props.truck.traverse(item => {
      if([...BOX_MESHES, ...BOX_LIGHTS].includes(item.name)) {
        this.boxMeshes.push(item)
        
        //@ts-ignore
        item.material = item.material.clone()

        if(SWITCH_MESHES.includes(item.name)) {
          const mat = {side: THREE.DoubleSide, opacity: 0, transparent: true}
          
          const topPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(2.6, 3, 32),
            new THREE.MeshBasicMaterial(mat)
          )

          topPlane.position.set(0, 1.7, 0.7)
          
          const bottomPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(2.6, 3, 32),
            new THREE.MeshBasicMaterial(mat)
          )

          bottomPlane.position.set(0, -1.2, 0.7)

          item.add(bottomPlane)
          item.add(topPlane)

          topPlane.name = 'top'
          bottomPlane.name = 'bottom'
          topPlane.userData.parent = item
          bottomPlane.userData.parent = item
          this.boxMeshes.push(topPlane, bottomPlane)
        }

      }
      if(BOX_LIGHTS.includes(item.name)) {
        item.visible = false
      }
    })

    const { onDown, onUp } = this
    
    this.props.bindRaycaster({
      meshes: [...this.boxMeshes],
      onDown, 
      onUp
    })

    this.mixer.addEventListener('finished', this.onAnimationFinished)
  }

  onAnimationFinished = (event: THREE.Event) => {
    this.props.onAnimationEnd({event, step: this.lastActiveStepData, switchType: this.switchClickedSide})
  }

  onDown = (object: THREE.Object3D) => {
    if(object && (object.userData.clickable || object.parent!.userData.clickable)) {
      const targetMesh = object.parent!.userData.clickable ? object.parent : object
      
      this.switchClickedSide = ['top', 'bottom'].includes(object.name) ? object.name : undefined
      
      const targetAction = this.actions.find(item => item.meshName === targetMesh!.name)
      
      if(targetAction) { 
        const targetLight = this.boxMeshes.find(item => item.name === targetAction.light)
        console.log()

        targetAction.action.reset()
        targetAction.action.play()
        if(targetLight) {
          if(targetLight.visible &&  targetLight.name !== 'SwitchEm4') {
            targetLight.visible = false 
          } else {
            targetLight.visible = true
          }
        }
      }
    }
  } 
  
  onUp = (object: THREE.Object3D) => {
    if(object) {
      
    }
  }
  
  update(deltaTime: number) {
    this.mixer.update(deltaTime)
  }

  initBox(data: ControllBox, activeStepData: ControllBoxStep) {
    BOX_LIGHTS.forEach(item => {
      this.props.truck.getObjectByName(item)!.visible = false
    })
    
    this.actions = data.steps
      .filter(item => ['click', 'switch'].includes(item.type))
      .map(item => ({
        clip: this.props.truckAnimations.find(a => a.name === item.clipName)!, 
        meshName: item.meshName!,
        light: item.light!,
      }))
      .map(({clip, meshName, light}) => ({action: this.mixer.clipAction(clip), meshName, light }))
      
    this.actions.forEach(item => {
      item.action.timeScale = 0.5
      item.action.loop = THREE.LoopOnce
    })


    this.hightLightItems([activeStepData.meshName || ''])    

    this.inited = true
  }

  reset() {
    BOX_LIGHTS.forEach(item => {
      this.props.truck.getObjectByName(item)!.visible = false
      //@ts-ignore
      // item.material.emissive.set(0x000000)
    })

    this.lastActiveStepData = undefined
    this.actions = []
    this.inited = false
  }

  hightLightItems(meshNames: string[]) {
    this.boxMeshes.forEach(item => {
      if(!meshNames.includes(item.name)) {
        item.userData.clickable = false
        item.children.forEach(item => item.userData.clickable = false)
      } else {
        item.userData.clickable = true
        item.children.forEach(item => item.userData.clickable = true)
      }
    })
  }

  initWaterTrack(ControllBoxStep: ControllBoxStep) {
    const waterPath = anime.path('#waterendpath')
    this.trackAction = this.knobMixer.clipAction(this.props.truckAnimations.find(item => item.name === ControllBoxStep.clipName)!)
    this.knobMixer.update(-this.knobMixer.time)
    this.trackAction.paused = true
    this.trackAction.clampWhenFinished = false
    this.trackAction.loop = THREE.LoopOnce
    this.trackAction.play()

    this.props.bindDom(this.props.truck.getObjectByName(ControllBoxStep.meshName!))
    
    this.tl = anime({
      targets: '.waterknob',
      translateX: waterPath('x'),
      translateY: waterPath('y'),
      rotate: waterPath('angle'),
      easing: 'linear',
      duration: 1,
      loop: true
    })
    
    this.tl.paused = true

    this.initDrag()
  }

  updateWaterTrack({y}: {y: number}) {
    const progress = Math.abs(y/200)
    this.tl.seek(progress)
    this.trackAction!.paused = false
    
    //@ts-ignore
    const duration = this.trackAction._clip.duration
    const targetTime = duration * (progress === 1 ? 0.99999 : progress)

    this.knobMixer!.update(-this.knobMixer!.time)
    this.knobMixer!.update(targetTime)

    if(progress === 1) {
      this.props.onAnimationEnd({})
    }
  }

  initDrag() {
    if(this.drag) this.drag[0].kill()

    const D = document.createElement('div')
    const self = this
    
    this.drag = Draggable.create(D, {
      trigger: '.waterknob',
      type: 'y',
      bounds:{ 
        minY:0,
        maxY: 200
      },
      onDrag: function() {
        self.updateWaterTrack(this)
      },
      onThrowUpdate: function() {
        self.updateWaterTrack(this)
      } 
    }) 
  }

  resetWaterTrack() {
    if(this.tl) {
      this.tl.reset() 
    }
  }

  updateBox(activeStepData: ControllBoxStep) {
    if(this.inited) {
      this.hightLightItems([activeStepData.meshName || ''])
      if(activeStepData.type === 'waterTrack') {
        this.initWaterTrack(activeStepData)
      } else {
        this.resetWaterTrack()
      }
      this.lastActiveStepData = activeStepData
    }
  }
}